import {Injectable, Injector, NgZone} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Subject, catchError, map, of, take} from 'rxjs';
import {environment} from '@env/environment';
import {LocalStorageService} from '@services/local-storage.service';
import {ToastService} from '../../toast/toast.service';
import {StackPanelService} from '@shared/stack-panel/stack-panel.service';
import {NotificationService} from '@notifications';
import {
  ITaskCenterTaskListItemBackend,
  ITaskCenterTaskListItem,
  ITaskCenterTaskMessage,
  TaskStatus,
} from './interface';
import {AutoLogoutService} from '../../controls/auto-logout/autologout.service';

const TASK_CENTER_WS_PATH = 'task-center';
const TASK_CENTER_EVENTS_STORAGE_KEY = 'task-center.events';
const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;
const FORBIDDEN_RESPONSE_CODE = 403;
const TASK_CENTER_TOAST_AUTO_HIDE_MS = 5000;

type ClientRequestEventFlag = {
  isClientRequest?: boolean;
};

export type TaskCenterTaskSentEvent = ClientRequestEventFlag & {
  type: 'TASK_SENT';
  taskId: number;
  assigneeId: number;
  authorId: number;
  authorName: string;
  taskTypeId: number;
  taskTypeName: string;
};

export type TaskCenterTaskStatusChangedEvent = ClientRequestEventFlag & {
  type: 'TASK_STATUS_CHANGED';
  taskId: number;
  status: TaskStatus;
  previousStatus: TaskStatus;
  actorId: number;
};

export type TaskCenterTaskMessageEvent = ClientRequestEventFlag & {
  type: 'TASK_MESSAGE';
  taskId: number;
  messageId: number;
  authorId: number;
};

export type TaskCenterTaskMessageEditedEvent = ClientRequestEventFlag & {
  type: 'TASK_MESSAGE_EDITED';
  taskId: number;
  messageId: number;
  authorId: number;
};

export type TaskCenterEvent =
  | TaskCenterTaskSentEvent
  | TaskCenterTaskStatusChangedEvent
  | TaskCenterTaskMessageEvent
  | TaskCenterTaskMessageEditedEvent;

export type StoredTaskCenterEvent = TaskCenterEvent & {
  eventId: number;
  receivedAt: string;
};

export type TaskCenterWsStatus = 'idle' | 'connecting' | 'connected' | 'closed' | 'error';
export type TaskCenterTaskUpdate = {
  event: StoredTaskCenterEvent & (TaskCenterTaskSentEvent | TaskCenterTaskStatusChangedEvent);
  task: ITaskCenterTaskListItem | null;
};
export type TaskCenterMessageUpdate = {
  event: StoredTaskCenterEvent & (TaskCenterTaskMessageEvent | TaskCenterTaskMessageEditedEvent);
  message: ITaskCenterTaskMessage | null;
};
type TTaskCenterPanelInitialTab = 'task' | 'conversation';

@Injectable({
  providedIn: 'root',
})
export class TaskCenterRealtimeService {
  private readonly eventsSubject = new BehaviorSubject<StoredTaskCenterEvent[]>([]);
  private readonly wsStatusSubject = new BehaviorSubject<TaskCenterWsStatus>('idle');
  private readonly taskListUpdateSubject = new Subject<TaskCenterTaskUpdate>();
  private readonly messagesUpdateSubject = new Subject<TaskCenterMessageUpdate>();
  private currentUserKey: string | null = null;
  private forbiddenHandled = false;
  private reconnectDelay = INITIAL_RECONNECT_DELAY;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = false;
  private socket: WebSocket | null = null;
  private readonly activeConversationTaskIds = new Set<number>();
  private readonly taskSnapshotsByTaskId = new Map<number, ITaskCenterTaskListItem>();

  readonly taskCenterEvents$ = this.eventsSubject.asObservable();
  readonly taskCenterEventCount$ = new BehaviorSubject<number>(0);
  readonly taskCenterWsStatus$ = this.wsStatusSubject.asObservable();
  readonly taskListUpdate$ = this.taskListUpdateSubject.asObservable();
  readonly messagesUpdate$ = this.messagesUpdateSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly toastService: ToastService,
    private readonly stackPanelService: StackPanelService,
    private readonly injector: Injector,
    private readonly notificationService: NotificationService,
    private readonly zone: NgZone,
    private readonly autoLogoutService: AutoLogoutService,
  ) {}

  connect(): void {
    if (!this.localStorageService.isAuthenticated()) {
      this.disconnect();
      return;
    }

    this.setCurrentUser();
    const url = this.buildTaskCenterWsUrl();
    if (!url) {
      return;
    }

    this.shouldReconnect = true;
    if (
      this.socket?.readyState === WebSocket.CONNECTING ||
      this.socket?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    this.clearReconnectTimer();
    this.wsStatusSubject.next('connecting');

    const socket = new WebSocket(url);
    this.socket = socket;

    socket.onopen = () => this.zone.run(() => {
      this.reconnectDelay = INITIAL_RECONNECT_DELAY;
      this.wsStatusSubject.next('connected');
    });

    socket.onmessage = (message) => this.zone.run(() => this.handleMessage(message));

    socket.onerror = (error) => this.zone.run(() => {
      this.wsStatusSubject.next('error');
      if (this.isForbiddenResponseEvent(error)) {
        this.handleForbiddenResponse();
      }
    });

    socket.onclose = (event) => this.zone.run(() => {
      if (this.socket === socket) {
        this.socket = null;
      }

      this.wsStatusSubject.next('closed');
      if (this.isForbiddenResponseEvent(event)) {
        this.handleForbiddenResponse();
        return;
      }

      this.scheduleReconnect();
    });
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    this.closeSocket();
    this.wsStatusSubject.next('idle');
  }

  openEvent(event: StoredTaskCenterEvent): void {
    this.removeTaskCenterEvent(event.eventId);
    this.openTask(event.taskId, this.isMessageEvent(event) ? 'conversation' : 'task');
  }

  registerConversationThread(taskId: number): () => void {
    this.activeConversationTaskIds.add(taskId);

    return () => {
      this.activeConversationTaskIds.delete(taskId);
    };
  }

  removeTaskCenterEvent(eventId: number): void {
    this.replaceCurrentUserEvents(this.eventsSubject.value.filter((event) => event.eventId !== eventId));
  }

  clearTaskCenterEvents(): void {
    this.replaceCurrentUserEvents([]);
  }

  getEventTitle(event: TaskCenterEvent): string {
    switch (event.type) {
      case 'TASK_SENT':
        return `${event.taskTypeName || 'Task'} #${event.taskId}`;
      case 'TASK_STATUS_CHANGED':
        return `Task #${event.taskId}`;
      case 'TASK_MESSAGE':
        return `New message on task #${event.taskId}`;
      case 'TASK_MESSAGE_EDITED':
        return `Message edited on task #${event.taskId}`;
    }
  }

  getEventBody(event: TaskCenterEvent): string {
    if (event.type === 'TASK_SENT') {
      return `${event.authorName || 'A user'} assigned a task`;
    }

    if (event.type === 'TASK_STATUS_CHANGED') {
      if (this.isTaskCompletedEvent(event)) {
        return event.isClientRequest ? 'Help Request has been resolved' : 'Task has been completed';
      }
      if (this.isTaskReturnedToClientEvent(event)) {
        return 'Task has been returned for updates';
      }
      return `Status changed to ${this.getStatusLabel(event.status)}`;
    }

    if (event.type === 'TASK_MESSAGE') {
      return 'A new message was received';
    }

    return 'A task message was updated';
  }

  private handleMessage(message: MessageEvent): void {
    try {
      const event = this.parseTaskCenterEvent(JSON.parse(String(message.data)));
      if (!event) {
        return;
      }

      const storedEvent = this.addTaskCenterEvent(event);
      this.handleTaskCenterEvent(storedEvent);
    } catch {
      // Ignore malformed websocket payloads.
    }
  }

  private handleTaskCenterEvent(event: StoredTaskCenterEvent): void {
    if (event.type === 'TASK_SENT' || event.type === 'TASK_STATUS_CHANGED') {
      this.readTask(event.taskId)
        .pipe(
          take(1),
          catchError(() => of(null)),
        )
        .subscribe((task) => {
          if (task) {
            this.taskSnapshotsByTaskId.set(task.id, this.cloneTask(task));
          }
          const update = {event, task};
          this.taskListUpdateSubject.next(update);
          this.showTaskListToast(update);
        });
      return;
    }

    this.readMessageUpdate(event)
      .pipe(take(1))
      .subscribe((update) => {
        this.messagesUpdateSubject.next(update);
        this.showMessageToast(update);
      });
  }

  private showTaskListToast(update: TaskCenterTaskUpdate): void {
    const {event, task} = update;

    this.toastService.show({
      header: this.getTaskToastTitle(event, task),
      message: this.getTaskToastBody(event, task),
      autoHideMs: TASK_CENTER_TOAST_AUTO_HIDE_MS,
      onClick: () => this.openEvent(event),
    });
  }

  private showMessageToast(update: TaskCenterMessageUpdate): void {
    const {event, message} = update;
    if (this.activeConversationTaskIds.has(event.taskId)) {
      this.removeTaskCenterEvent(event.eventId);
      return;
    }

    if (message?.isAuthor) {
      return;
    }

    this.toastService.show({
      header: message?.authorName || this.getEventTitle(event),
      message: message?.body || (event.type === 'TASK_MESSAGE' ? 'Sent an attachment' : this.getEventBody(event)),
      autoHideMs: TASK_CENTER_TOAST_AUTO_HIDE_MS,
      onClick: () => this.openEvent(event),
    });
  }

  private readMessageUpdate(event: StoredTaskCenterEvent & (TaskCenterTaskMessageEvent | TaskCenterTaskMessageEditedEvent)) {
    return this.readMessage(event.taskId, event.messageId)
      .pipe(
        take(1),
        catchError(() => of(null)),
        map((message) => ({
          event,
          message,
        })),
      );
  }

  private addTaskCenterEvent(event: TaskCenterEvent): StoredTaskCenterEvent {
    if (!this.currentUserKey) {
      this.setCurrentUser();
    }

    const events = this.eventsSubject.value;
    const nextEventId = events.reduce(
      (eventId, storedEvent) => Math.max(eventId, storedEvent.eventId + 1),
      1,
    );
    const storedEvent: StoredTaskCenterEvent = {
      ...event,
      eventId: nextEventId,
      receivedAt: new Date().toISOString(),
    };

    this.replaceCurrentUserEvents([...events, storedEvent]);
    return storedEvent;
  }

  private setCurrentUser(): void {
    const user = this.localStorageService.getCurrentUserDetails();
    const userKey = user?.userId ? String(user.userId) : user?.username || null;
    if (!userKey || this.currentUserKey === userKey) {
      return;
    }

    this.currentUserKey = userKey;
    this.eventsSubject.next(this.getStoredEventsByUser()[userKey] || []);
    this.taskCenterEventCount$.next(this.eventsSubject.value.length);
  }

  private replaceCurrentUserEvents(events: StoredTaskCenterEvent[]): void {
    if (!this.currentUserKey) {
      this.setCurrentUser();
    }
    if (!this.currentUserKey) {
      return;
    }

    const eventsByUser = this.getStoredEventsByUser();
    eventsByUser[this.currentUserKey] = events;
    this.localStorageService.setValue(TASK_CENTER_EVENTS_STORAGE_KEY, JSON.stringify(eventsByUser));
    this.eventsSubject.next(events);
    this.taskCenterEventCount$.next(events.length);
  }

  private getStoredEventsByUser(): Record<string, StoredTaskCenterEvent[]> {
    const rawValue = this.localStorageService.getValue(TASK_CENTER_EVENTS_STORAGE_KEY);
    if (!rawValue) {
      return {};
    }

    try {
      return JSON.parse(rawValue) || {};
    } catch {
      return {};
    }
  }

  private readTask(taskId: number) {
    return this.http.get<ITaskCenterTaskListItemBackend>(
      `${environment.API_URL}/tasks/${taskId}`,
    ).pipe(
      // Keep this local to avoid pulling the lazy TaskCenterService into the initial bundle.
      // The full task center still uses TaskCenterService for normal task operations.
      map((task) => this.toFrontendTask(task)),
    );
  }

  private readMessage(taskId: number, messageId: number) {
    return this.http.get<ITaskCenterTaskMessage>(
      `${environment.API_URL}/tasks/${taskId}/messages/${messageId}`,
    );
  }

  private openTask(taskId: number, initialTab: TTaskCenterPanelInitialTab): void {
    const cachedTask = this.taskSnapshotsByTaskId.get(taskId);
    if (cachedTask) {
      this.showTaskPanel(this.cloneTask(cachedTask), initialTab);
      return;
    }

    this.readTask(taskId)
      .pipe(take(1))
      .subscribe({
        next: (task) => {
          this.taskSnapshotsByTaskId.set(task.id, this.cloneTask(task));
          this.showTaskPanel(task, initialTab);
        },
        error: (error) => this.notificationService.error(error),
      });
  }

  private showTaskPanel(task: ITaskCenterTaskListItem, initialTab: TTaskCenterPanelInitialTab): void {
    import('./task/task.component')
      .then(({TaskComponent}) => {
        this.stackPanelService.show(TaskComponent, {
          panelWidth: '65%',
          parentInjector: this.injector,
          initialState: {
            task,
            initialTab,
          },
        });
      })
      .catch(() => this.notificationService.error('Failed to open task'));
  }

  private toFrontendTask(task: ITaskCenterTaskListItemBackend): ITaskCenterTaskListItem {
    const {settings, ...rest} = task;
    let parsedSettings = {};
    if (settings) {
      try {
        parsedSettings = JSON.parse(settings);
      } catch {
        parsedSettings = {};
      }
    }

    return {
      ...rest,
      settings: parsedSettings,
    };
  }

  private cloneTask(task: ITaskCenterTaskListItem): ITaskCenterTaskListItem {
    return {
      ...task,
      settings: {...task.settings},
      messages: [...(task.messages || [])],
      attachmentsList: [...(task.attachmentsList || [])],
    };
  }

  private buildTaskCenterWsUrl(): string | null {
    const baseUrl = environment.WS_URL;
    if (!baseUrl) {
      return null;
    }

    const url = `${baseUrl.replace(/\/$/, '')}/${TASK_CENTER_WS_PATH}`;
    if (/^wss?:\/\//i.test(url)) {
      return url;
    }
    if (/^https?:\/\//i.test(url)) {
      return url.replace(/^http/i, 'ws');
    }
    if (typeof window === 'undefined') {
      return url;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${url.startsWith('/') ? url : `/${url}`}`;
  }

  private parseTaskCenterEvent(data: unknown): TaskCenterEvent | null {
    if (!data || typeof data !== 'object') {
      return null;
    }

    const event = data as Partial<TaskCenterEvent>;
    switch (event.type) {
      case 'TASK_SENT':
        return this.isNumber(event.taskId) &&
          this.isNumber(event.assigneeId) &&
          this.isNumber(event.authorId) &&
          typeof event.authorName === 'string' &&
          this.isNumber(event.taskTypeId) &&
          typeof event.taskTypeName === 'string'
          ? event as TaskCenterEvent
          : null;
      case 'TASK_STATUS_CHANGED':
        return this.isNumber(event.taskId) &&
          this.isNumber(event.status) &&
          this.isNumber(event.previousStatus) &&
          this.isNumber(event.actorId)
          ? event as TaskCenterEvent
          : null;
      case 'TASK_MESSAGE':
      case 'TASK_MESSAGE_EDITED':
        return this.isNumber(event.taskId) &&
          this.isNumber(event.messageId) &&
          this.isNumber(event.authorId)
          ? event as TaskCenterEvent
          : null;
      default:
        return null;
    }
  }

  private isMessageEvent(event: TaskCenterEvent): event is TaskCenterTaskMessageEvent | TaskCenterTaskMessageEditedEvent {
    return event.type === 'TASK_MESSAGE' || event.type === 'TASK_MESSAGE_EDITED';
  }

  private scheduleReconnect(): void {
    if (!this.shouldReconnect || this.reconnectTimer) {
      return;
    }

    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, MAX_RECONNECT_DELAY);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) {
      return;
    }

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private closeSocket(): void {
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState !== WebSocket.CLOSED) {
      socket.close();
    }
  }

  private handleForbiddenResponse(): void {
    if (this.forbiddenHandled) {
      return;
    }

    this.forbiddenHandled = true;
    this.disconnect();
    this.localStorageService.clearStorage();
    this.autoLogoutService.stop();
    window.location.href = '/login';
    setTimeout(() => {
      this.forbiddenHandled = false;
    }, 1000);
  }

  private isForbiddenResponseEvent(event: unknown): boolean {
    const responseEvent = event as {code?: unknown; message?: unknown; reason?: unknown};
    const message = typeof responseEvent.message === 'string' ? responseEvent.message : '';
    const reason = typeof responseEvent.reason === 'string' ? responseEvent.reason : '';

    return responseEvent.code === FORBIDDEN_RESPONSE_CODE || /\b403\b/.test(`${message} ${reason}`);
  }

  private isTaskCompletedEvent(event: TaskCenterTaskStatusChangedEvent): boolean {
    return event.status === TaskStatus.Completed;
  }

  private isTaskReturnedToClientEvent(event: TaskCenterTaskStatusChangedEvent): boolean {
    return !event.isClientRequest &&
      event.previousStatus === TaskStatus.Checking &&
      event.status === TaskStatus.Clarified;
  }

  private getTaskToastTitle(
    event: TaskCenterTaskSentEvent | TaskCenterTaskStatusChangedEvent,
    task: ITaskCenterTaskListItem | null,
  ): string {
    const fallbackName = event.isClientRequest ? 'Help Request' : 'Task';
    const typeName = task?.typeName || (event.type === 'TASK_SENT' ? event.taskTypeName : fallbackName);
    return `${typeName || fallbackName} #${event.taskId}`;
  }

  private getTaskToastBody(
    event: TaskCenterTaskSentEvent | TaskCenterTaskStatusChangedEvent,
    task: ITaskCenterTaskListItem | null,
  ): string {
    if (event.type === 'TASK_SENT') {
      return task?.description || 'New task has been assigned';
    }

    return this.getEventBody(event);
  }

  private getStatusLabel(status: TaskStatus): string {
    switch (status) {
      case TaskStatus.Draft:
        return 'Draft';
      case TaskStatus.Sent:
        return 'Sent';
      case TaskStatus.Clarification:
        return 'Clarification';
      case TaskStatus.Clarified:
        return 'Clarified';
      case TaskStatus.Checking:
        return 'Checking';
      case TaskStatus.Completed:
        return 'Completed';
      default:
        return 'Updated';
    }
  }

  private isNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }
}
